import { AlertTriangle, BookOpen, ExternalLink, HardHat, ListChecks, PhoneCall, ScrollText } from 'lucide-react';
import type { EquipmentInduction } from '../../types/induction';

type Content = Pick<EquipmentInduction,
  'summary' | 'hazards' | 'ppe' | 'operating_rules' | 'practical_checks' | 'emergency_procedure' | 'licence_note' | 'manual_url'>;

interface Props {
  induction: Content;
  /** Show what the assessor watches for. Off for a trainee reading their own. */
  showPracticalChecks?: boolean;
  requiredCertifications?: { id: number; name: string }[];
}

/**
 * An equipment induction's safety requirements, laid out the same way for
 * every machine: what could hurt you and what stops it, what to wear, the
 * rules, and what to do when it goes wrong.
 */
export default function InductionContent({ induction, showPracticalChecks = true, requiredCertifications = [] }: Props) {
  const hazards = induction.hazards || [];
  const ppe = induction.ppe || [];
  const rules = induction.operating_rules || [];
  const checks = induction.practical_checks || [];

  return (
    <div className="space-y-5 text-sm text-neutral-700">
      {induction.summary && (
        <div className="whitespace-pre-line break-words">{induction.summary}</div>
      )}

      {(induction.licence_note || requiredCertifications.length > 0) && (
        <Section icon={<ScrollText className="h-4 w-4 text-primary-600" />} title="Licences and certifications">
          {requiredCertifications.length > 0 && (
            <ul className="list-disc pl-5 space-y-1">
              {requiredCertifications.map(c => <li key={c.id} className="break-words">{c.name}</li>)}
            </ul>
          )}
          {induction.licence_note && <p className="break-words mt-1">{induction.licence_note}</p>}
        </Section>
      )}

      {hazards.length > 0 && (
        <Section icon={<AlertTriangle className="h-4 w-4 text-warning-600" />} title="Hazards and controls">
          <ul className="space-y-2">
            {hazards.map((h, i) => (
              <li key={i} className="rounded-lg border border-neutral-200 p-3">
                <div className="font-medium text-neutral-900 break-words">{h.hazard}</div>
                {h.control && <div className="mt-1 text-neutral-600 break-words">{h.control}</div>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {ppe.length > 0 && (
        <Section icon={<HardHat className="h-4 w-4 text-primary-600" />} title="What to wear">
          <div className="flex flex-wrap gap-2">
            {ppe.map((p, i) => (
              <span key={i} className="max-w-full break-words rounded-full bg-primary-50 px-3 py-1 text-primary-800">{p}</span>
            ))}
          </div>
        </Section>
      )}

      {rules.length > 0 && (
        <Section icon={<BookOpen className="h-4 w-4 text-primary-600" />} title="Rules">
          <ul className="list-disc pl-5 space-y-1">
            {rules.map((r, i) => <li key={i} className="break-words">{r}</li>)}
          </ul>
        </Section>
      )}

      {showPracticalChecks && checks.length > 0 && (
        <Section icon={<ListChecks className="h-4 w-4 text-primary-600" />} title="What the assessor watches you do">
          <ul className="list-disc pl-5 space-y-1">
            {checks.map((c, i) => <li key={i} className="break-words">{c}</li>)}
          </ul>
        </Section>
      )}

      {induction.emergency_procedure && (
        <Section icon={<PhoneCall className="h-4 w-4 text-error-600" />} title="In an emergency">
          <p className="whitespace-pre-line break-words">{induction.emergency_procedure}</p>
        </Section>
      )}

      {induction.manual_url && (
        <a href={induction.manual_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-primary-700 hover:underline break-all">
          <ExternalLink className="h-4 w-4 shrink-0" /> Operator manual
        </a>
      )}
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 font-semibold text-neutral-900">{icon}{title}</h3>
      {children}
    </section>
  );
}
